import React, { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { PageContainer } from '..';
import { useBrandName } from '../../../hooks';
import { ProjectDto, ProjectComparisonDto } from '../../../models';
import {
	SelectedProject,
	SelectedComparison,
	AppState,
	AppThunkDispatch,
	BosatlasActions,
	fetchBosatlas
} from '../../../redux';
import { Header1 } from '../../atoms';
import { ComparisonAndProjectOverview } from '../../organisms';

export interface IComparisonProjectSelectorProps {
	disabled?: boolean;
	projectSelection: SelectedProject[];
	comparisonSelection: SelectedComparison[];
	onProjectSelect: (project: SelectedProject, selected: boolean) => void;
	onComparisonSelect: (
		comparison: SelectedComparison,
		selected: boolean
	) => void;
}

export interface IComparisonProjectSelectorStateProps {
	projects: ProjectDto[];
	comparisons: ProjectComparisonDto[];
	fetchBosatlas: () => void;
}

function ComparisonProjectSelectorView(
	props: IComparisonProjectSelectorProps &
		IComparisonProjectSelectorStateProps
): ReactElement {
	const {
		disabled,
		projects,
		comparisons,
		fetchBosatlas,
		projectSelection,
		comparisonSelection,
		onProjectSelect,
		onComparisonSelect
	} = props;

	const { t } = useTranslation('general');
	const brandName: string = useBrandName();

	useEffect(() => {
		fetchBosatlas();
	}, []);

	function isProjectSelected(project: ProjectDto): boolean {
		return (
			projectSelection.find(
				(p: SelectedProject) => p.id === project.id
			) !== undefined
		);
	}

	function isComparisonSelected(comparison: ProjectComparisonDto): boolean {
		return (
			comparisonSelection.find(
				(c: SelectedComparison) => c.id === comparison.id
			) !== undefined
		);
	}

	function onProjectClicked(project: ProjectDto): void {
		const isSelected: boolean = isProjectSelected(project);

		const newProject: SelectedProject = {
			id: project.id,
			title: project.title as string,
			thumbnail: project.assetThumbnailUrl as string
		};

		onProjectSelect(newProject, !isSelected);
	}

	function onComparisonClicked(comparison: ProjectComparisonDto): void {
		const isSelected: boolean = isComparisonSelected(comparison);

		const newComparison: SelectedComparison = {
			id: comparison.id,
			title: comparison.title as string,
			firstAssetThumbnailUrl: comparison.firstAssetThumbnailUrl,
			secondAssetThumbnailUrl: comparison.secondAssetThumbnailUrl,
			thirdAssetThumbnailUrl: comparison.thirdAssetThumbnailUrl,
			fourthAssetThumbnailUrl: comparison.fourthAssetThumbnailUrl
		};

		onComparisonSelect(newComparison, !isSelected);
	}

	return (
		<PageContainer id="bosatlas-map-selector">
			<Header1>{t('myAtlas', { brandName })}</Header1>
			<ComparisonAndProjectOverview
				disabled={disabled}
				projects={projects}
				comparisons={comparisons}
				selectable
				disablePreview
				isProjectSelected={isProjectSelected}
				isComparisonSelected={isComparisonSelected}
				onComparisonClick={onComparisonClicked}
				onProjectClick={onProjectClicked}
				onProjectPreview={onProjectClicked}
			/>
		</PageContainer>
	);
}

function mapStateToProps(state: AppState) {
	return {
		projects: state.bosatlas.projects ? state.bosatlas.projects : [],
		comparisons: state.bosatlas.projectComparisons
			? state.bosatlas.projectComparisons
			: []
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasActions>) {
	return {
		fetchBosatlas: () => dispatch(fetchBosatlas())
	};
}

export const ComparisonProjectSelector = connect(
	mapStateToProps,
	mapDispatchToProps
)(ComparisonProjectSelectorView);
